/**
 * **Add or edit a category** — its name, description and order (C25, Wave 27D,
 * T-1.31; ADR-0054 §4). A category could not be edited at all before Wave 27.
 *
 * Its address on the website (the slug) is made from the first name and never
 * follows a rename, so a link that worked last year keeps working; the dialog
 * says so rather than offering a field that would not change it. An order left
 * empty on a new category puts it last.
 */

import { Button, Dialog, Input, Textarea } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import { DialogActions, FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import { type ServiceCategory, services } from '../../lib/api/services'

interface Form {
  name: string
  description: string
  order: string
}

/** A whole number, or `undefined` for *leave it* — never `NaN` on the wire. */
export function orderValue(text: string): number | undefined {
  const trimmed = text.trim()
  if (!/^-?\d+$/.test(trimmed)) return undefined
  return Number(trimmed)
}

export function CategoryDialog({
  open,
  category,
  onClose,
  onSaved,
}: {
  open: boolean
  /** `null` adds one. */
  category: ServiceCategory | null
  onClose: () => void
  onSaved: (sentence: string) => Promise<void> | void
}) {
  const [form, setForm] = useState<Form>({ name: '', description: '', order: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setForm(
      category
        ? {
            name: category.name,
            description: category.description ?? '',
            order: String(category.display_order),
          }
        : { name: '', description: '', order: '' },
    )
  }, [open, category])

  const orderOk = form.order.trim() === '' || orderValue(form.order) !== undefined
  const ready = form.name.trim() !== '' && orderOk

  const save = async () => {
    setBusy(true)
    setError(null)
    const order = orderValue(form.order)
    try {
      if (category) {
        await services.updateCategory(category.id, {
          name: form.name.trim(),
          description: form.description.trim() || null,
          ...(order !== undefined ? { display_order: order } : {}),
        })
        await onSaved(`“${form.name.trim()}” is saved.`)
      } else {
        await services.createCategory({
          name: form.name.trim(),
          ...(form.description.trim() ? { description: form.description.trim() } : {}),
          ...(order !== undefined ? { display_order: order } : {}),
        })
        await onSaved(`“${form.name.trim()}” is added.`)
      }
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That category could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={category ? `Edit ${category.name}` : 'Add a category'}
      error={error}
      width="lg"
    >
      <div className={FORM_GRID}>
        <FormRow label="Name" htmlFor="category-name">
          <Input
            id="category-name"
            value={form.name}
            maxLength={120}
            placeholder="GST"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            hint={
              category
                ? `Renaming keeps its address on your website (“${category.slug}”), so links to it keep working.`
                : 'A heading your services sit under — GST, Income tax, ROC.'
            }
          />
        </FormRow>
        <FormRow label="Description" htmlFor="category-description" optional>
          <Textarea
            id="category-description"
            rows={2}
            maxLength={500}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </FormRow>
        <FormRow label="Order" htmlFor="category-order" optional>
          <Input
            id="category-order"
            inputMode="numeric"
            value={form.order}
            onChange={(e) => setForm({ ...form, order: e.target.value })}
            error={orderOk ? null : 'A whole number — lower comes first.'}
            hint={orderOk ? 'Lower comes first, on your website and in every list. Empty puts a new one last.' : undefined}
          />
        </FormRow>
      </div>
      <DialogActions>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void save()} disabled={!ready} loading={busy}>
          {category ? 'Save category' : 'Add category'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
